import { createContext, useContext, useState, useEffect, useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";

export interface UnifiedAccount {
  id: number;
  platformId: number;
  externalAccountId: string;
  accountName: string;
  currency: string;
  timezone: string;
  status: string;
  isDemo: boolean;
  presentation?: 'developer-preview';
  syncStatus: string | null;
  platform: {
    id: number;
    slug: string;
    name: string;
    color: string;
    icon: string | null;
  } | null;
}

interface DashboardAccountContextType {
  selectedAccountIds: number[];
  setSelectedAccountIds: (ids: number[]) => void;
  toggleAccount: (id: number) => void;
  selectAll: () => void;
  selectPlatform: (slug: string) => void;
  togglePlatform: (slug: string) => void;
  allAccounts: UnifiedAccount[];
  selectedAccounts: UnifiedAccount[];
  activePlatforms: string[];
  isLoading: boolean;
  isAllSelected: boolean;
  brandId: number | null;
  setBrandId: (id: number | null) => void;
}

const DashboardAccountContext = createContext<DashboardAccountContextType>({
  selectedAccountIds: [],
  setSelectedAccountIds: () => {},
  toggleAccount: () => {},
  selectAll: () => {},
  selectPlatform: () => {},
  togglePlatform: () => {},
  allAccounts: [],
  selectedAccounts: [],
  activePlatforms: [],
  isLoading: true,
  isAllSelected: true,
  brandId: null,
  setBrandId: () => {},
});

export function DashboardAccountProvider({ children }: { children: ReactNode }) {
  const [selectedAccountIds, setSelectedAccountIds] = useState<number[]>(() => {
    try {
      const stored = localStorage.getItem("dashboard-selected-accounts");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const [brandId, setBrandIdState] = useState<number | null>(() => {
    try {
      const stored = localStorage.getItem("dashboard-brand-filter");
      return stored ? Number(stored) : null;
    } catch {
      return null;
    }
  });

  const setBrandId = (id: number | null) => {
    setBrandIdState(id);
    if (id !== null) {
      localStorage.setItem("dashboard-brand-filter", String(id));
    } else {
      localStorage.removeItem("dashboard-brand-filter");
    }
  };

  const { data: accounts = [], isLoading } = useQuery<UnifiedAccount[]>({
    queryKey: ["/api/ad-accounts"],
    retry: false,
    refetchInterval: 30000,
    staleTime: 10000,
  });

  useEffect(() => {
    if (accounts.length > 0 && selectedAccountIds.length === 0) {
      const allIds = accounts.map(a => a.id);
      setSelectedAccountIds(allIds);
    }
  }, [accounts]);

  useEffect(() => {
    localStorage.setItem("dashboard-selected-accounts", JSON.stringify(selectedAccountIds));
  }, [selectedAccountIds]);

  const toggleAccount = (id: number) => {
    setSelectedAccountIds(prev => {
      if (prev.includes(id)) {
        const next = prev.filter(i => i !== id);
        return next.length === 0 ? accounts.map(a => a.id) : next;
      }
      return [...prev, id];
    });
  };

  const selectAll = () => {
    setSelectedAccountIds(accounts.map(a => a.id));
  };

  const selectPlatform = (slug: string) => {
    const platformAccounts = accounts.filter(a => a.platform?.slug === slug);
    setSelectedAccountIds(platformAccounts.map(a => a.id));
  };

  const togglePlatform = (slug: string) => {
    const platformAccountIds = accounts.filter(a => a.platform?.slug === slug).map(a => a.id);
    const currentlyHasPlatform = platformAccountIds.every(id => selectedAccountIds.includes(id));

    if (currentlyHasPlatform) {
      const next = selectedAccountIds.filter(id => !platformAccountIds.includes(id));
      if (next.length === 0) {
        setSelectedAccountIds(accounts.map(a => a.id));
      } else {
        setSelectedAccountIds(next);
      }
    } else {
      const merged = new Set([...selectedAccountIds, ...platformAccountIds]);
      setSelectedAccountIds(Array.from(merged));
    }
  };

  const selectedAccounts = useMemo(() =>
    accounts.filter(a => selectedAccountIds.includes(a.id)),
    [accounts, selectedAccountIds]
  );

  const activePlatforms = useMemo(() => {
    const slugs = new Set(selectedAccounts.map(a => a.platform?.slug).filter(Boolean));
    return Array.from(slugs) as string[];
  }, [selectedAccounts]);

  const isAllSelected = selectedAccountIds.length === accounts.length && accounts.length > 0;

  return (
    <DashboardAccountContext.Provider value={{
      selectedAccountIds,
      setSelectedAccountIds,
      toggleAccount,
      selectAll,
      selectPlatform,
      togglePlatform,
      allAccounts: accounts,
      selectedAccounts,
      activePlatforms,
      isLoading,
      isAllSelected,
      brandId,
      setBrandId,
    }}>
      {children}
    </DashboardAccountContext.Provider>
  );
}

export function useDashboardAccounts() {
  return useContext(DashboardAccountContext);
}
